<template>
  <div class="flex flex-col items-center gap-4">
    <DsCard padding="lg" class="w-full text-center">
      <div
        class="text-text-caption-md mb-3 flex items-center justify-center gap-2 font-semibold uppercase tracking-wide text-grey-60"
      >
        <DsIcon name="bookmark" size="sm" />
        Show this to collect
      </div>

      <!--
        Rendered here rather than fetched from the partner. They do expose a PNG
        (GET /partner/claims/:code/qr.png) but it needs the partner token, which
        cannot ship in the app — it would have to be proxied through our API,
        and it would fail exactly when a member is standing at a counter with no
        signal. Encoding the code we already hold works offline and needs
        nothing new. Open question for their developer: does their scanner
        accept a QR of the plain code, or only their own rendering?
      -->
      <div class="flex justify-center">
        <qrcode-vue :value="code" :size="200" level="H" render-as="svg" :margin="1" />
      </div>

      <!-- The code stays in words underneath: a scanner can be broken, a
           counter can be busy, and the alphabet excludes I, L, O and U
           precisely so it can be read aloud without ambiguity. -->
      <div class="text-text-caption-md mt-4 uppercase tracking-wide text-grey-60">
        Or read this out
      </div>
      <div
        class="font-mono text-text-body-xl whitespace-nowrap font-black tracking-[0.08em] text-grey-100"
      >
        {{ code }}
      </div>

      <DsButton class="mt-4" variant="secondary" size="48" shape="pill" @click="copyCode">
        <span class="flex flex-row items-center justify-center gap-2">
          <DsIcon v-if="copied" name="check" size="sm" />
          {{ copied ? 'Copied' : 'Copy code' }}
        </span>
      </DsButton>

      <!-- The label change is invisible to a screen reader on its own. -->
      <div class="sr-only" role="status" aria-live="polite">{{ copied ? 'Code copied' : '' }}</div>
    </DsCard>
  </div>
</template>

<script>
import QrcodeVue from 'qrcode.vue';
import {
  Button as DsButton,
  Card as DsCard,
  Icon as DsIcon,
} from '@team-hogc/hogc-one-design-system';
import { copyText } from '@/utils/clipboard';
import { triggerImpact } from '@/utils/haptics';

export default {
  name: 'PartnerCollectionCode',
  components: { QrcodeVue, DsButton, DsCard, DsIcon },
  props: {
    code: {
      type: String,
      required: true,
    },
  },
  data() {
    return { copied: false, resetTimerId: null };
  },
  beforeUnmount() {
    if (this.resetTimerId) clearTimeout(this.resetTimerId);
  },
  methods: {
    copyCode() {
      // Confirm on the press. Awaiting the clipboard first put the whole
      // acknowledgement behind an async round trip, which reads as lag.
      this.copied = true;
      triggerImpact('light');
      if (this.resetTimerId) clearTimeout(this.resetTimerId);
      this.resetTimerId = setTimeout(() => {
        this.copied = false;
      }, 2000);

      // Via the shared util, not navigator.clipboard directly: it keeps an
      // execCommand fallback that this app's install base still needs, and a
      // bare Clipboard API call would silently do nothing there. The code is on
      // screen and readable either way, so a failure needs no error state.
      copyText(this.code);
    },
  },
};
</script>
